import { useTranslations } from 'next-intl';
import { type FC, useCallback } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import clsx from 'clsx';
import { z } from 'zod';

import { type GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { type GateGroupOption, GateGroupSelect } from '@/components/common/select/GateGroupSelect';
import { OperationPeriodSelect } from '@/components/common/select/OperationPeriodSelect';
import { type WithClassName } from '@/types/react';

import { DashboardV2PeriodType } from '../../../../../../../../../../../../../generated/graphql';

import styles from './SalesCountComparisonChartFilter.module.scss';

type SalesCountComparisonChartFilterProps = WithClassName & {
  defaultValues?: Partial<SalesCountComparisonChartFilter>;
  gateGroupDetails?: GateGroupDetails;
  onSubmit: SubmitHandler<SalesCountComparisonChartFilter>;
};

const useFormSchema = (gateGroupDetails?: GateGroupDetails) => {
  const t = useTranslations('charts.filters');

  const gateGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    currency_id: z.optional(z.string()),
  });

  const gateGroupsSchema = z.array(gateGroupSchema);

  return z.object({
    period: z.nativeEnum(DashboardV2PeriodType),
    gateGroup: gateGroupDetails ? gateGroupSchema : z.optional(gateGroupSchema),
    gateGroups: gateGroupDetails ? gateGroupsSchema : gateGroupsSchema.length(2, t('gateGroup.errors.selectTwoGates')),
  });
};

export type SalesCountComparisonChartFilter = z.infer<ReturnType<typeof useFormSchema>>;

export const SalesCountComparisonChartFilter: FC<SalesCountComparisonChartFilterProps> = (props) => {
  const t = useTranslations('charts.filters');
  const tButtons = useTranslations('buttons');

  const { className, defaultValues, gateGroupDetails, onSubmit } = props;

  const form = useForm<SalesCountComparisonChartFilter>({
    resolver: zodResolver(useFormSchema(gateGroupDetails)),
    defaultValues,
  });

  const { handleSubmit, register } = form;

  const gateGroupsFilter = useCallback(
    (option: GateGroupOption) => {
      return option.id !== gateGroupDetails?.id;
    },
    [gateGroupDetails?.id]
  );

  return (
    <FormProvider {...form}>
      <form className={clsx(styles.root, className)} onSubmit={handleSubmit(onSubmit)}>
        <FormSelect
          {...register('period')}
          variant="box"
          label={t('period.label')}
          component={OperationPeriodSelect}
          withHalfYear={false}
        />

        {gateGroupDetails ? (
          <FormSelect
            {...register('gateGroup')}
            variant="box"
            label={t('gateGroup.label')}
            component={GateGroupSelect}
            filter={gateGroupsFilter}
          />
        ) : (
          <FormSelect
            {...register('gateGroups')}
            variant="box"
            isMulti
            label={t('gateGroup.label')}
            placeholder={t('gateGroup.placeholder.twoGates')}
            component={GateGroupSelect}
            filter={gateGroupsFilter}
          />
        )}

        <FormSubmitButton className={styles.submitButton}>{tButtons('search')}</FormSubmitButton>
      </form>
    </FormProvider>
  );
};
